import React from 'react';
import { getSupabaseIconUrl } from '@/utils/supabaseIcons';
import { ICON_MAPPING, EMOJI_MAPPING, IconType, EmojiType } from '@/utils/iconMapping';
import { getIconCropConfig } from '@/utils/iconScaling';

interface CustomIconProps {
  /** Lucide icon name to replace */
  type?: IconType;
  /** Emoji character to replace */
  emoji?: EmojiType;
  /** Direct icon filename if not in mapping */
  icon?: string;
  /** CSS classes for styling */
  className?: string;
  /** Alt text for accessibility */
  alt?: string;
  /** Apply white background cropping styles */
  cropWhiteBackground?: boolean;
}

/**
 * CustomIcon Component
 *
 * Replaces Lucide icons and emojis with custom Supabase Storage icons
 * Supports automatic mapping, manual icon specification, and white background cropping
 *
 * ACCESSIBILITY: Enforces minimum sizes and provides error handling
 */
export const CustomIcon: React.FC<CustomIconProps> = ({
  type,
  emoji,
  icon,
  className = 'w-8 h-8',
  alt,
  cropWhiteBackground = true, // Changed to TRUE by default - all our icons need cropping
}) => {
  const [imageError, setImageError] = React.useState(false);

  // Determine icon filename
  const iconFile = React.useMemo(() => {
    if (icon) return icon;
    if (type && ICON_MAPPING[type]) return ICON_MAPPING[type];
    if (emoji && EMOJI_MAPPING[emoji]) return EMOJI_MAPPING[emoji];

    // Fallback - try to guess from type name
    if (type) return `icon-${type}.png`;

    console.warn('CustomIcon: No icon mapping found', { type, emoji, icon });
    return 'icon-help-circle.png'; // Default fallback
  }, [type, emoji, icon]);

  // Generate alt text
  const altText = React.useMemo(() => {
    if (alt) return alt;
    if (emoji) return `${emoji} icon`;
    if (type) return `${type.replace('-', ' ')} icon`;
    return 'Custom icon';
  }, [alt, emoji, type]);

  // Handle image load errors
  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.error(`Icon failed to load: ${iconFile}`, { type, emoji, icon });
    setImageError(true);
    // Try fallback icon
    if (iconFile !== 'icon-help-circle.png') {
      e.currentTarget.src = getSupabaseIconUrl('icon-help-circle.png');
    }
  };

  // Fallback display if image fails completely
  if (imageError && iconFile === 'icon-help-circle.png') {
    return (
      <div className={`flex items-center justify-center bg-gray-100 rounded ${className}`}>
        <span className="text-gray-400 text-xs">?</span>
      </div>
    );
  }

  // Apply cropping for white backgrounds - Simple scale for colored RGB icons
  if (cropWhiteBackground) {
    // For large intro icons (w-20+), use minimal scaling
    const isLargeIcon = className.includes('w-28') || className.includes('w-32') || className.includes('w-20');

    // Conservative scale to avoid cutoff
    const scale = isLargeIcon ? 1.08 : 1.25;

    return (
      <div className={`flex items-center justify-center bg-white rounded-xl ${className}`}>
        <img
          src={getSupabaseIconUrl(iconFile)}
          alt={altText}
          className="w-full h-full object-contain"
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'center'
          }}
          loading="lazy"
          onError={handleError}
        />
      </div>
    );
  }

  // Standard icon display
  return (
    <img
      src={getSupabaseIconUrl(iconFile)}
      alt={altText}
      className={`${className} min-w-[20px] min-h-[20px]`}
      loading="lazy"
      onError={handleError}
    />
  );
};

/**
 * Navigation Icon Components
 * Pre-configured icons for common navigation patterns
 * ACCESSIBILITY: Minimum 24px (w-6 h-6) for interactive icons
 */
export const NavIcon = {
  ChevronLeft: (props: Omit<CustomIconProps, 'type'>) => (
    <CustomIcon type="chevron-left" alt="Previous" className="w-6 h-6 min-w-[24px] min-h-[24px]" {...props} />
  ),
  ChevronRight: (props: Omit<CustomIconProps, 'type'>) => (
    <CustomIcon type="chevron-right" alt="Next" className="w-6 h-6 min-w-[24px] min-h-[24px]" {...props} />
  ),
  ArrowLeft: (props: Omit<CustomIconProps, 'type'>) => (
    <CustomIcon type="arrow-left" alt="Back" className="w-6 h-6 min-w-[24px] min-h-[24px]" {...props} />
  ),
  ArrowRight: (props: Omit<CustomIconProps, 'type'>) => (
    <CustomIcon type="arrow-right" alt="Forward" className="w-6 h-6 min-w-[24px] min-h-[24px]" {...props} />
  ),
  Home: (props: Omit<CustomIconProps, 'type'>) => (
    <CustomIcon type="home" alt="Home" className="w-6 h-6 min-w-[24px] min-h-[24px]" {...props} />
  ),
};

/**
 * Action Icon Components
 * Pre-configured icons for common actions with better default sizing
 * ACCESSIBILITY: Minimum 24px (w-6 h-6) for interactive icons
 */
export const ActionIcon = {
  Check: (props: Omit<CustomIconProps, 'type'>) => (
    <CustomIcon type="check-circle" alt="Complete" className="w-6 h-6 min-w-[24px] min-h-[24px]" {...props} />
  ),
  Target: (props: Omit<CustomIconProps, 'type'>) => (
    <CustomIcon type="target" alt="Goal" className="w-6 h-6 min-w-[24px] min-h-[24px]" {...props} />
  ),
  Lightbulb: (props: Omit<CustomIconProps, 'type'>) => (
    <CustomIcon type="lightbulb" alt="Idea" className="w-6 h-6 min-w-[24px] min-h-[24px]" {...props} />
  ),
  Sparkles: (props: Omit<CustomIconProps, 'type'>) => (
    <CustomIcon type="sparkles" alt="Enhancement" className="w-6 h-6 min-w-[24px] min-h-[24px]" {...props} />
  ),
  Zap: (props: Omit<CustomIconProps, 'type'>) => (
    <CustomIcon type="zap" alt="Speed" className="w-6 h-6 min-w-[24px] min-h-[24px]" {...props} />
  ),
  X: (props: Omit<CustomIconProps, 'type'>) => (
    <CustomIcon type="x-circle" alt="Close" className="w-6 h-6 min-w-[24px] min-h-[24px]" {...props} />
  ),
  ChevronLeft: (props: Omit<CustomIconProps, 'type'>) => (
    <CustomIcon type="chevron-left" alt="Back" className="w-6 h-6 min-w-[24px] min-h-[24px]" {...props} />
  ),
};

/**
 * Character Avatar Components
 */
export const CharacterAvatar = {
  Maya: (props: { className?: string }) => (
    <img
      src={getSupabaseIconUrl('maya-avatar.png')}
      alt="Maya Rodriguez"
      className={`rounded-full ${props.className || 'w-12 h-12'} object-cover`}
      loading="lazy"
      onError={(e) => {
        console.error('Maya avatar failed to load, using fallback');
        // Fallback to colored circle
        e.currentTarget.style.display = 'none';
        const fallback = document.createElement('div');
        fallback.className = `rounded-full bg-purple-100 flex items-center justify-center ${props.className || 'w-12 h-12'}`;
        fallback.innerHTML = '<span class="text-purple-700 font-bold text-lg">M</span>';
        e.currentTarget.parentElement?.appendChild(fallback);
      }}
    />
  ),
  Lyra: (props: { className?: string }) => (
    <img
      src={getSupabaseIconUrl('lyra-avatar.png')}
      alt="Lyra AI"
      className={`rounded-full ${props.className || 'w-12 h-12'} object-cover`}
      loading="lazy"
      onError={(e) => {
        console.error('Lyra avatar failed to load');
        e.currentTarget.src = getSupabaseIconUrl('icon-bot.png');
      }}
    />
  ),
  Sofia: (props: {
    className?: string;
    emotion?: 'default' | 'concerned' | 'confident' | 'joyful' | 'proud' | 'raised-hands' | 'shrug' | 'welcoming';
  }) => {
    const getAvatarFile = (emotion: string) => {
      switch (emotion) {
        case 'concerned': return 'sofia-concerned.png';
        case 'confident': return 'sofia-confident.png';
        case 'joyful': return 'sofia-joyful.png';
        case 'proud': return 'sofia-proud.png';
        case 'raised-hands': return 'sofia-raised-hands.png';
        case 'shrug': return 'sofia-shrug.png';
        case 'welcoming': return 'sofia-welcoming.png';
        case 'default': return 'sofia-confident.png'; // Map default to confident (no generic sofia-avatar.png exists)
        default: return 'sofia-confident.png'; // Fallback
      }
    };

    const getEmotionColor = (emotion: string) => {
      switch (emotion) {
        case 'concerned': return 'bg-orange-100 text-orange-700';
        case 'confident': return 'bg-blue-100 text-blue-700';
        case 'joyful': return 'bg-yellow-100 text-yellow-700';
        case 'proud': return 'bg-green-100 text-green-700';
        case 'raised-hands': return 'bg-pink-100 text-pink-700';
        case 'shrug': return 'bg-gray-100 text-gray-700';
        case 'welcoming': return 'bg-purple-100 text-purple-700';
        default: return 'bg-rose-100 text-rose-700';
      }
    };

    return (
      <img
        src={getSupabaseIconUrl(getAvatarFile(props.emotion || 'default'))}
        alt={`Sofia Martinez - ${props.emotion || 'default'}`}
        className={`rounded-full ${props.className || 'w-12 h-12'} object-cover`}
        loading="lazy"
        onError={(e) => {
          console.error(`Sofia avatar failed to load: ${getAvatarFile(props.emotion || 'default')}`);
          console.log(`Generated URL: ${e.currentTarget.src}`);
          // Replace with colored circle fallback
          e.currentTarget.style.display = 'none';
          const fallback = document.createElement('div');
          fallback.className = `rounded-full flex items-center justify-center ${getEmotionColor(props.emotion || 'default')} ${props.className || 'w-12 h-12'}`;
          fallback.innerHTML = '<span class="font-bold text-lg">S</span>';
          e.currentTarget.parentElement?.appendChild(fallback);
        }}
      />
    );
  },
};

/**
 * Icon Size Presets
 * Standard sizing options for consistent icon display
 * ACCESSIBILITY: All sizes ≥24px (w-6 h-6) for interactive elements
 * Decorative inline icons can be w-5 h-5 (20px) minimum
 */
export const IconSize = {
  xs: 'w-5 h-5',      // 20px - Minimum for decorative inline icons
  sm: 'w-5 h-5',      // 20px - Small decorative icons
  md: 'w-6 h-6',      // 24px - Default size (MINIMUM for interactive)
  lg: 'w-8 h-8',      // 32px - For cards and prominent UI
  xl: 'w-10 h-10',    // 40px - For large buttons
  '2xl': 'w-12 h-12', // 48px - For hero sections
  '3xl': 'w-16 h-16', // 64px - For major visual elements
} as const;

/**
 * Card Icon Component
 * Optimized for visibility in neomorphic cards
 */
export const CardIcon: React.FC<{
  type: IconType;
  className?: string;
  alt?: string;
}> = ({ type, className = IconSize.lg, alt }) => (
  <div className={`flex items-center justify-center ${className} bg-white/30 rounded-lg p-2`}>
    <CustomIcon
      type={type}
      className="w-full h-full opacity-90"
      alt={alt}
    />
  </div>
);

export default CustomIcon;